// src/utils/paymentShared.js
// Shared helpers + UI pieces for the Payment History and Payment Commission pages,
// so both pages always agree on what "refunded", "pending refund", dates, etc. mean.
import React from "react";
import { Row, Col, Modal, Button, Pagination } from "react-bootstrap";
import { formatCurrency } from "./format";
import { Colors } from "../constants/Colors";

export const PER_PAGE = 15;

export const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover: "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

/* ================================================================== */
/* DATA HELPERS                                                       */
/* ================================================================== */

const lower = (v) => String(v ?? "").toLowerCase();

export const ONLINE_METHODS = ["gcash", "qrph"];

export const STATUS_META = {
  succeeded: { color: Colors.SUCCESS, label: "Succeeded" },
  paid: { color: Colors.SUCCESS, label: "Paid" },
  pending: { color: Colors.WARNING, label: "Pending" },
  processing: { color: Colors.WARNING, label: "Processing" },
  failed: { color: Colors.RED, label: "Failed" },
  cancelled: { color: Colors.GRAY, label: "Cancelled" },
  refunded: { color: Colors.INFO, label: "Refunded" },
};

export const getStatusMeta = (status) => {
  const m = STATUS_META[lower(status)];
  if (m) return { ...m, bg: `${m.color}1f` };
  return { color: Colors.GRAY, bg: `${Colors.GRAY}26`, label: status || "—" };
};

export const getPaymentType = (p) => {
  if (p?.payment_type) return p.payment_type;
  const payload = p?.payload;
  if (payload && typeof payload === "object") {
    if (payload.payment_type === "cancellation_fee") return "cancellation_fee";
    if (payload.type === "cancellation_fee") return "cancellation_fee";
  }
  return "rental";
};

// Rental-level refund flags only describe the online RENTAL payment. They must
// never be applied to a cancellation fee, a cash/manual payment, or a failed retry.
const isOnlineRentalPayment = (p) =>
  lower(p?.status) === "succeeded" &&
  ONLINE_METHODS.includes(lower(p?.payment_method)) &&
  getPaymentType(p) !== "cancellation_fee";

/** Backend truth: payload.refund_status === 'succeeded' (set by finalizeRefund). */
export const isRefunded = (p) => {
  if (lower(p?.payload?.refund_status) === "succeeded") return true;
  if (lower(p?.status) === "refunded") return true;
  return isOnlineRentalPayment(p) && lower(p?.rental?.refund_status) === "refunded";
};

/** 'refunded' | 'pending' | 'failed' | null */
export const getRefundState = (p) => {
  if (isRefunded(p)) return "refunded";
  const payloadStatus = lower(p?.payload?.refund_status);
  if (payloadStatus === "failed") return "failed";
  if (payloadStatus === "pending" || payloadStatus === "processing") return "pending";
  if (isOnlineRentalPayment(p) && lower(p?.rental?.refund_status) === "pending") {
    return "pending";
  }
  return null;
};

export const getRefundInfo = (p) => {
  const payload = p?.payload && typeof p.payload === "object" ? p.payload : {};
  const rental = p?.rental || {};
  return {
    refund_id: payload.refund_id || null,
    refund_status: payload.refund_status || rental.refund_status || null,
    refund_reference: rental.refund_reference || payload.refund_reference || null,
    refunded_at: payload.refunded_at || rental.refunded_at || null,
    refund_reason:
      payload.refund_reason || payload.notes || rental.cancellation_reason || null,
    refund_amount:
      payload.refund_amount != null ? Number(payload.refund_amount) : (p?.amount ?? null),
    wallet_refund_deducted: !!payload.wallet_refund_deducted,
    clawback_actual: Number(payload.refund_clawback_actual || 0),
    clawback_amount: Number(payload.refund_clawback_amount || 0),
    shortfall: Number(payload.refund_clawback_shortfall || 0),
  };
};

export const getPaymentId = (p) => p?.payment_id ?? p?.id;

/** The date the list is filtered and sorted by (backend: COALESCE(paid_at, created_at)). */
export const getPaymentDate = (p) => p?.paid_at || p?.created_at;

export const getPassengerName = (p) =>
  p?.rental?.passenger
    ? `${p.rental.passenger.first_name} ${p.rental.passenger.last_name}`
    : "N/A";

export const getOperatorName = (p) =>
  p?.rental?.operator
    ? `${p.rental.operator.first_name} ${p.rental.operator.last_name}`
    : "N/A";

export const getJeepneyLabel = (p) => {
  const j = p?.rental?.jeepney;
  if (!j) return "N/A";
  const model = j.model || j.vehicle_type || "";
  const plate = j.license_plate ? ` (${j.license_plate})` : "";
  return (model + plate).trim() || "N/A";
};

export const getInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length || name === "N/A") return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${first}${last}`.toUpperCase();
};

export const formatCommissionRate = (rate) =>
  rate !== null && rate !== undefined && rate !== ""
    ? `${parseFloat(rate).toFixed(1)}%`
    : "0.0%";

export const formatShortDate = (v) => {
  if (!v) return "N/A";
  try {
    return new Date(v).toLocaleDateString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "N/A";
  }
};

export const formatDateTime = (v) => {
  if (!v) return "N/A";
  try {
    return new Date(v).toLocaleString("en-PH");
  } catch {
    return "N/A";
  }
};

export const getDefaultDateRange = () => {
  const today = new Date();
  const from = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
  const toIso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from: toIso(from), to: toIso(today) };
};

/** Normalises the Laravel paginator response shapes. */
export const parseList = (payload) => {
  if (!payload || payload.success === false) {
    return { rows: [], pagination: null, error: true };
  }
  let rows = [];
  let pagination = null;
  if (payload.data && Array.isArray(payload.data.data)) {
    rows = payload.data.data;
    pagination = {
      current_page: payload.data.current_page || 1,
      last_page: payload.data.last_page || 1,
      per_page: payload.data.per_page || PER_PAGE,
      total: payload.data.total ?? rows.length,
    };
  } else if (Array.isArray(payload.data)) {
    rows = payload.data;
    pagination = {
      current_page: 1,
      last_page: 1,
      per_page: rows.length,
      total: rows.length,
    };
  } else if (Array.isArray(payload.pagination?.data)) {
    rows = payload.pagination.data;
    pagination = payload.pagination;
  }
  return { rows, pagination, error: false };
};

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

export const PaymentsStyles = () => (
  <style>{`
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }

    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }

    .bk-stat { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 16px 18px; display: flex; align-items: center; gap: 12px; transition: transform .18s ease, box-shadow .18s ease; height: 100%; }
    .bk-stat:hover { transform: translateY(-2px); box-shadow: ${T.shadowHover}; }
    .bk-stat-icon { width: 44px; height: 44px; border-radius: 11px; display: grid; place-items: center; font-size: 19px; flex-shrink: 0; }
    .bk-stat-val { font-size: 21px; font-weight: 700; line-height: 1; color: ${Colors.TEXT_DARK}; }
    .bk-stat-val.text { font-size: 15px; }
    .bk-stat-lbl { font-size: 11px; color: ${Colors.TEXT_LIGHT}; text-transform: uppercase; letter-spacing: .05em; margin-top: 4px; font-weight: 600; }

    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-header { padding: 16px 22px; border-bottom: ${T.border}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; background: #fff; }
    .bk-card-header h5 { margin: 0; font-weight: 600; color: ${Colors.TEXT_DARK}; font-size: 15.5px; display: flex; align-items: center; gap: 8px; }
    .bk-card-header small { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }

    .bk-toolbar { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 14px; box-shadow: ${T.shadow}; margin-bottom: 18px; }
    .bk-input { border: 1px solid transparent !important; background: ${Colors.LIGHT_GRAY} !important; border-radius: ${T.radiusSm}px !important; padding: 9px 13px !important; font-size: 13.5px; transition: all .18s; color: ${Colors.TEXT_DARK}; height: auto !important; }
    .bk-input:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }
    .bk-input-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; margin-bottom: 6px; display: block; }
    .bk-filter-btn { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm}px !important; padding: 9px 13px !important; font-size: 13.5px; font-weight: 500; text-align: left; display: flex; align-items: center; justify-content: space-between; height: auto !important; min-height: 42px; }
    .bk-filter-btn:hover, .bk-filter-btn:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: none !important; color: ${Colors.TEXT_DARK} !important; }
    .bk-filter-btn::after { margin-left: auto; }

    .bk-table { margin: 0; }
    .bk-table thead th { background: ${Colors.LIGHT_GRAY}; border: none; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; font-weight: 700; padding: 13px 18px; white-space: nowrap; }
    .bk-table tbody td { padding: 15px 18px; border-color: ${Colors.BORDER_GRAY}; vertical-align: middle; font-size: 13.5px; color: ${Colors.TEXT_DARK}; }
    .bk-table tbody tr { transition: background .15s; cursor: pointer; }
    .bk-table tbody tr:hover { background: ${Colors.LIGHT_GRAY}55; }
    .bk-table tbody tr.row-cancellation { background: ${Colors.WARNING}0a; }
    .bk-table tbody tr.row-cancellation:hover { background: ${Colors.WARNING}14; }
    .bk-table tbody tr.row-refund-pending { background: ${Colors.INFO}0a; }
    .bk-table tbody tr.row-refund-pending:hover { background: ${Colors.INFO}14; }
    .bk-table tbody tr.row-refunded { background: ${Colors.INFO}08; }
    .bk-table tbody tr.row-refunded:hover { background: ${Colors.INFO}12; }
    .bk-table tbody td.col-actions, .bk-table thead th.col-actions { width: 1%; white-space: nowrap; text-align: right; }
    .bk-name { font-weight: 600; color: ${Colors.TEXT_DARK}; }
    .bk-sub { font-size: 12px; color: ${Colors.TEXT_LIGHT}; margin-top: 2px; }

    .bk-avatar { width: 34px; height: 34px; border-radius: 10px; background: linear-gradient(135deg, ${Colors.GRADIENT_START}, ${Colors.GRADIENT_END}); color: #fff; font-weight: 600; font-size: 13px; display: grid; place-items: center; flex-shrink: 0; }
    .bk-id-chip { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 11px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_LIGHT}; padding: 3px 7px; border-radius: 5px; }

    .bk-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; line-height: 1.4; }
    .bk-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
    .bk-pill.no-dot::before { display: none; }
    .bk-method-pill { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
    .bk-type-chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 6px; font-size: 11px; font-weight: 600; }
    .bk-refund-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 999px; font-size: 11px; font-weight: 600; }
    .bk-money { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-weight: 700; }
    .bk-money.muted { color: ${Colors.TEXT_LIGHT}; text-decoration: line-through; }

    .bk-empty { text-align: center; padding: 60px 20px; }
    .bk-empty-icon { width: 80px; height: 80px; margin: 0 auto 18px; border-radius: 50%; background: ${Colors.LIGHT_GRAY}; display: grid; place-items: center; font-size: 34px; color: ${Colors.GRAY}; }
    .bk-empty h5 { font-weight: 600; margin-bottom: 6px; }

    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    .bk-btn-primary { background: ${Colors.PRIMARY} !important; border: none !important; color: #fff !important; border-radius: ${T.radiusSm}px !important; font-weight: 600; padding: 8px 16px; font-size: 13.5px; transition: all .18s; }
    .bk-btn-primary:hover { background: #e87a2e !important; transform: translateY(-1px); box-shadow: 0 6px 16px ${Colors.PRIMARY}44; }
    .bk-btn-primary:disabled { opacity: .6 !important; cursor: not-allowed !important; transform: none !important; box-shadow: none !important; }
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm}px !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-ghost:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }
    .bk-btn-ghost:disabled { opacity: .6 !important; cursor: not-allowed !important; }
    .bk-btn-success { background: ${Colors.SUCCESS} !important; border: none !important; color: #fff !important; border-radius: ${T.radiusSm}px !important; font-weight: 600; padding: 8px 16px; font-size: 13.5px; transition: all .18s; }
    .bk-btn-success:hover { background: #1e7e34 !important; transform: translateY(-1px); box-shadow: 0 6px 16px ${Colors.SUCCESS}44; }
    .bk-btn-success:disabled { opacity: .6 !important; cursor: not-allowed !important; transform: none !important; box-shadow: none !important; }

    .bk-section { background: ${Colors.LIGHT_GRAY}; border-radius: ${T.radiusSm}px; padding: 16px; margin-bottom: 14px; }
    .bk-section:last-child { margin-bottom: 0; }
    .bk-section-title { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; margin-bottom: 12px; display: flex; align-items: center; gap: 7px; }
    .bk-section-title i { color: ${Colors.PRIMARY}; font-size: 14px; }
    .bk-info-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px dashed ${Colors.BORDER_GRAY}; font-size: 13px; gap: 12px; }
    .bk-info-row:last-child { border-bottom: none; }
    .bk-info-label { color: ${Colors.TEXT_LIGHT}; flex-shrink: 0; }
    .bk-info-value { color: ${Colors.TEXT_DARK}; font-weight: 600; text-align: right; max-width: 65%; word-break: break-word; }

    .modal-content { border-radius: ${T.radius}px; border: none; box-shadow: 0 24px 60px rgba(16,24,40,.18); }
    .modal-header { border-bottom: ${T.border}; padding: 18px 22px; }
    .modal-title { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 16px; }
    .modal-body { padding: 20px 22px; }
    .modal-footer { border-top: ${T.border}; padding: 14px 22px; }

    .page-item.active .page-link { background: ${Colors.PRIMARY} !important; border-color: ${Colors.PRIMARY} !important; color: #fff !important; }
    .page-link { color: ${Colors.TEXT_DARK} !important; border-color: ${Colors.BORDER_GRAY} !important; }
    .page-link:hover { background: ${Colors.LIGHT_GRAY} !important; }

    .dropdown-menu { border-radius: ${T.radiusSm}px; border: ${T.border}; box-shadow: ${T.shadow}; padding: 6px; }
    .dropdown-item { border-radius: 6px; padding: 8px 12px; font-size: 13px; }
    .dropdown-item.active { background: ${Colors.PRIMARY}1a !important; color: ${Colors.TEXT_DARK} !important; }
  `}</style>
);

/* ================================================================== */
/* SMALL UI PIECES                                                    */
/* ================================================================== */

export const StatusPill = ({ status }) => {
  const m = getStatusMeta(status);
  return (
    <span className="bk-pill" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

export const MethodPill = ({ method }) => {
  const key = lower(method);
  const meta =
    {
      gcash: { color: Colors.INFO, label: "GCash" },
      qrph: { color: Colors.SECONDARY_BLUE || Colors.INFO, label: "QR Ph" },
      cash: { color: Colors.SUCCESS, label: "Cash" },
      manual: { color: "#b88a00", label: "Manual" },
      card: { color: Colors.SECONDARY_BLUE || Colors.INFO, label: "Card" },
    }[key] || { color: Colors.GRAY, label: (method || "—").toUpperCase() };
  return (
    <span
      className="bk-method-pill"
      style={{ color: meta.color, background: `${meta.color}1f` }}
    >
      {meta.label}
    </span>
  );
};

export const RatePill = ({ rate }) => (
  <span
    className="bk-pill no-dot"
    style={{
      color: Colors.TEXT_LIGHT,
      background: Colors.LIGHT_GRAY,
      border: T.border,
    }}
  >
    {formatCommissionRate(rate)}
  </span>
);

export const TypeChip = ({ type }) =>
  type === "cancellation_fee" ? (
    <span
      className="bk-type-chip"
      style={{ color: "#b88a00", background: `${Colors.WARNING}26` }}
    >
      <i className="bi bi-exclamation-circle" style={{ fontSize: 11 }} />
      Cancellation Fee
    </span>
  ) : (
    <span
      className="bk-type-chip"
      style={{ color: Colors.TEXT_LIGHT, background: `${Colors.GRAY}18` }}
    >
      Rental
    </span>
  );

export const RefundBadge = ({ state, amount }) => {
  if (!state) return null;
  const meta = {
    refunded: {
      color: Colors.INFO,
      icon: "bi-arrow-counterclockwise",
      label: "Refunded",
      title: `Full refund of ${formatCurrency(amount)} issued to passenger`,
    },
    pending: {
      color: Colors.INFO,
      icon: "bi-hourglass-split",
      label: "Refund pending",
      title: "Refund requested; not yet confirmed by PayMongo",
    },
    failed: {
      color: Colors.RED,
      icon: "bi-exclamation-triangle",
      label: "Refund failed",
      title: "PayMongo could not complete the refund. It can be retried from the booking.",
    },
  }[state];
  return (
    <span
      className="bk-refund-badge"
      style={{ color: meta.color, background: `${meta.color}1f` }}
      title={meta.title}
    >
      <i className={`bi ${meta.icon}`} style={{ fontSize: 11 }} />
      {meta.label}
    </span>
  );
};

export const Avatar = ({ name }) => (
  <div className="bk-avatar">{getInitials(name)}</div>
);

/** Row of stat cards in the same style as the Payouts page. */
export const StatsRow = ({ cards, loading }) => (
  <Row className="g-3 mb-4">
    {cards.map((s) => (
      <Col key={s.k} xs={6} md={4} lg={2}>
        <div className="bk-stat">
          <div
            className="bk-stat-icon"
            style={{ background: `${s.c}1f`, color: s.c }}
          >
            <i className={`bi ${s.i}`} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              className={`bk-stat-val${s.isText ? " text" : ""}`}
              title={s.title}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {loading ? (
                <span
                  className="bk-skel d-inline-block"
                  style={{ height: 18, width: 56 }}
                />
              ) : (
                s.v
              )}
            </div>
            <div className="bk-stat-lbl">{s.k}</div>
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

export const PaginationBar = ({ pagination, loading, onPage }) => {
  if (pagination.last_page <= 1) return null;
  const items = [];
  const max = 5;
  const current = pagination.current_page;
  const totalPages = pagination.last_page;
  let start = Math.max(1, current - Math.floor(max / 2));
  const end = Math.min(totalPages, start + max - 1);
  if (end - start + 1 < max) start = Math.max(1, end - max + 1);
  for (let n = start; n <= end; n++) {
    items.push(
      <Pagination.Item key={n} active={n === current} onClick={() => onPage(n)}>
        {n}
      </Pagination.Item>,
    );
  }
  return (
    <div
      className="d-flex justify-content-between align-items-center flex-wrap gap-2"
      style={{ padding: "14px 20px", borderTop: T.border }}
    >
      <small className="text-muted">
        Page {current} of {totalPages} · {pagination.total} records
      </small>
      <Pagination className="mb-0">
        <Pagination.First
          disabled={current === 1 || loading}
          onClick={() => onPage(1)}
        />
        <Pagination.Prev
          disabled={current === 1 || loading}
          onClick={() => onPage(current - 1)}
        />
        {items}
        <Pagination.Next
          disabled={current === totalPages || loading}
          onClick={() => onPage(current + 1)}
        />
        <Pagination.Last
          disabled={current === totalPages || loading}
          onClick={() => onPage(totalPages)}
        />
      </Pagination>
    </div>
  );
};

/* ================================================================== */
/* DETAILS MODAL (used by both pages)                                 */
/* ================================================================== */

export const PaymentDetailsModal = ({ payment, show, onHide }) => {
  const p = payment;
  let body = null;

  if (p) {
    const isFee = getPaymentType(p) === "cancellation_fee";
    const refundState = getRefundState(p);
    const refunded = refundState === "refunded";
    const info = getRefundInfo(p);
    const muted = refunded ? "muted" : "";

    const row = (label, value) => (
      <div className="bk-info-row">
        <span className="bk-info-label">{label}</span>
        <span className="bk-info-value">{value}</span>
      </div>
    );

    body = (
      <>
        <div className="bk-section">
          <div className="bk-section-title">
            <i className="bi bi-info-circle" /> Overview
          </div>
          {row(
            "Payment ID",
            <span className="bk-id-chip">#{getPaymentId(p)}</span>,
          )}
          {row(
            "Rental ID",
            <span className="bk-id-chip">#{p.rental_id}</span>,
          )}
          {p.payment_intent_id &&
            row(
              "PayMongo reference",
              <span className="bk-id-chip">{p.payment_intent_id}</span>,
            )}
          {row("Type", <TypeChip type={getPaymentType(p)} />)}
          {row(
            "Status",
            <div className="d-flex flex-column align-items-end gap-1">
              <StatusPill status={refunded ? "refunded" : p.status} />
              <RefundBadge state={refundState} amount={info.refund_amount} />
            </div>,
          )}
          {row("Method", <MethodPill method={p.payment_method} />)}
          {row("Created", formatDateTime(p.created_at))}
          {p.paid_at && row("Paid at", formatDateTime(p.paid_at))}
        </div>

        <div className="bk-section">
          <div className="bk-section-title">
            <i className="bi bi-people" /> Parties
          </div>
          {row("Customer", getPassengerName(p))}
          {row("Operator", getOperatorName(p))}
          {p.rental?.jeepney && row("Jeepney", getJeepneyLabel(p))}
        </div>

        <div className="bk-section">
          <div className="bk-section-title">
            <i className="bi bi-cash-coin" /> Financials
          </div>
          {row(
            "Amount",
            <span className={`bk-money ${muted}`}>{formatCurrency(p.amount)}</span>,
          )}
          {!isFee && (
            <>
              {row(
                `Platform commission (${formatCommissionRate(p.commission_rate)})`,
                <span className={`bk-money ${muted}`}>
                  {formatCurrency(p.commission_amount)}
                </span>,
              )}
              {row(
                "Operator earnings",
                <span className={`bk-money ${muted}`}>
                  {formatCurrency(p.operator_earnings)}
                </span>,
              )}
            </>
          )}
          {isFee &&
            row(
              "Note",
              <span style={{ color: "#b88a00", fontWeight: 500 }}>
                Penalty income. No commission, not credited to the operator wallet.
              </span>,
            )}
        </div>

        {refundState && (
          <div className="bk-section">
            <div className="bk-section-title">
              <i className="bi bi-arrow-return-left" /> Refund
            </div>
            {row(
              "Refund status",
              <RefundBadge state={refundState} amount={info.refund_amount} />,
            )}
            {info.refund_id &&
              row("Refund ID", <span className="bk-id-chip">{info.refund_id}</span>)}
            {info.refund_reference && row("Reference", info.refund_reference)}
            {info.refund_amount != null &&
              row(
                "Refund amount",
                <span className="bk-money" style={{ color: Colors.INFO }}>
                  {formatCurrency(info.refund_amount)}
                </span>,
              )}
            {info.refunded_at && row("Refunded at", formatDateTime(info.refunded_at))}
            {info.refund_reason && row("Reason", info.refund_reason)}
            {info.wallet_refund_deducted &&
              row("Wallet deducted", <span className="text-success">Yes</span>)}
            {info.clawback_actual > 0 &&
              row(
                "Clawback (actual)",
                <span className="bk-money">{formatCurrency(info.clawback_actual)}</span>,
              )}
            {info.clawback_amount > 0 &&
              row(
                "Clawback (expected)",
                <span className="bk-money">{formatCurrency(info.clawback_amount)}</span>,
              )}
            {info.shortfall > 0 &&
              row(
                "Shortfall (added to commission owed)",
                <span className="bk-money text-danger">
                  {formatCurrency(info.shortfall)}
                </span>,
              )}
          </div>
        )}
      </>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: `${Colors.PRIMARY}1f`,
                color: Colors.PRIMARY,
                display: "grid",
                placeItems: "center",
              }}
            >
              <i className="bi bi-receipt" />
            </span>
            Payment details{p ? ` · #${getPaymentId(p)}` : ""}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{body}</Modal.Body>
      <Modal.Footer>
        <Button className="bk-btn-ghost" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};